/** One-shot Socket.IO list-subscription ACK fence with a bounded fail-safe. */

export const SPORTSNEW_LIST_SUBSCRIPTION_ACK_TIMEOUT_MS = 1000;

const timeoutValue = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? Math.max(100, Math.min(3000, Math.floor(parsed)))
    : SPORTSNEW_LIST_SUBSCRIPTION_ACK_TIMEOUT_MS;
};

function emitSportsnewListEventWithAckFence({
  event,
  socket,
  subscription,
  isCurrent = () => true,
  onSettled,
  timeoutMs = SPORTSNEW_LIST_SUBSCRIPTION_ACK_TIMEOUT_MS,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) {
  let settled = false;
  let timer = null;

  const finish = (outcome, ack = null, error = null) => {
    if (settled) return false;
    settled = true;
    if (timer != null) clearTimer(timer);
    timer = null;
    if (typeof isCurrent === 'function' && !isCurrent()) return false;
    onSettled?.({ outcome, ack, error });
    return true;
  };

  if (typeof socket?.emit !== 'function' || !subscription) {
    finish('error', null, new Error('SPORTSNEW_LIST_SUBSCRIPTION_UNAVAILABLE'));
    return Object.freeze({ cancel() {}, settled: () => true });
  }

  timer = setTimer(() => finish('timeout'), timeoutValue(timeoutMs));
  try {
    socket.emit(event, subscription, ack => finish('ack', ack));
  } catch (error) {
    finish('error', null, error);
  }

  return Object.freeze({
    cancel() {
      if (settled) return;
      settled = true;
      if (timer != null) clearTimer(timer);
      timer = null;
    },
    settled: () => settled,
  });
}

export function emitSportsnewListSubscriptionWithAckFence(options = {}) {
  return emitSportsnewListEventWithAckFence({
    ...options,
    event: 'subscribe:sportsnew:list',
  });
}

export function emitSportsnewListHeartbeatWithAckFence(options = {}) {
  return emitSportsnewListEventWithAckFence({
    ...options,
    event: 'heartbeat:sportsnew:list',
  });
}

export default Object.freeze({
  emitSportsnewListSubscriptionWithAckFence,
  emitSportsnewListHeartbeatWithAckFence,
  SPORTSNEW_LIST_SUBSCRIPTION_ACK_TIMEOUT_MS,
});
